/**
 * index.html bevat een standaard description / og:description / twitter:description.
 * Pagina's die via react-helmet-async hun eigen tag zetten, krijgen daardoor twee tags
 * en zoekmachines pakken vaak de eerste (de homepage-tekst).
 *
 * Deze sync houdt de statische tag gelijk aan de Helmet-tag (data-rh) zolang die bestaat,
 * en zet de standaardtekst terug zodra de pagina geen eigen tag meer heeft.
 */
const SELECTORS = [
  { attr: "name", key: "description" },
  { attr: "property", key: "og:description" },
  { attr: "name", key: "twitter:description" },
] as const;

export function initHeadMetaSync() {
  if (typeof document === "undefined") return;
  const head = document.head;

  const defaults = new Map<Element, string>();
  for (const { attr, key } of SELECTORS) {
    const el = head.querySelector(`meta[${attr}="${key}"]:not([data-rh])`);
    if (el) defaults.set(el, el.getAttribute("content") ?? "");
  }
  if (defaults.size === 0) return;

  // Elements we overwrote with Helmet content; only those get their default back.
  // Pages that set the static tag themselves (querySelector + setAttribute) are left alone.
  const overwritten = new Set<Element>();

  const sync = () => {
    for (const { attr, key } of SELECTORS) {
      const staticEl = head.querySelector(`meta[${attr}="${key}"]:not([data-rh])`);
      if (!staticEl) continue;
      const helmetEl = head.querySelector(`meta[${attr}="${key}"][data-rh]`);
      if (helmetEl) {
        const target = helmetEl.getAttribute("content") ?? "";
        if (staticEl.getAttribute("content") !== target) staticEl.setAttribute("content", target);
        overwritten.add(staticEl);
      } else if (overwritten.has(staticEl)) {
        overwritten.delete(staticEl);
        const def = defaults.get(staticEl);
        if (def != null) staticEl.setAttribute("content", def);
      }
    }
  };

  const observer = new MutationObserver(sync);
  observer.observe(head, { childList: true, subtree: true, attributes: true, attributeFilter: ["content"] });
  sync();
}
